import React from 'react';

import { Formik, FormikErrors, Form as FormikForm } from 'formik';

import { Alert, Button, Panel } from '@shared/components/bootstrap';
import { FormikCheckboxFormGroup } from '@shared/components/fields/formik/formik_checkbox_form_group';
import { FormikDatePickerFormGroup } from '@shared/components/fields/formik/formik_date_picker_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikNumberFormGroup } from '@shared/components/fields/formik/formik_number_input_group';
import { FormikSelectFormGroup } from '@shared/components/fields/formik/formik_select_form_group';
import { Spacer } from '@shared/components/helpers';

import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import { ThirdPartyTransfer__Input, useThirdPartyTransferFormDataQuery } from '@admin/schema';

import { FormikTimePickerFormGroup } from '../fields/formik/formik_time_picker_form_group';

export type FormValues = Omit<ThirdPartyTransfer__Input, 'accountID'> & { allItemsAcknowledged: boolean };

export const ThirdPartyTransferForm: React.FC<{
  save(input: FormValues): void;
  kind: 'create' | 'update';
  defaults?: Partial<FormValues>;
  saving?: boolean;
  error?: string | null;
}> = ({ save, kind, defaults, saving, error }) => {
  const { data: formData } = useThirdPartyTransferFormDataQuery({ client });

  const validate = (values: FormValues): FormikErrors<FormValues> => {
    const errors: FormikErrors<FormValues> = {};
    if (!values.originWarehouseID) errors.originWarehouseID = 'Required';
    if (!values.destinationWarehouseID) errors.destinationWarehouseID = 'Required';
    if (!values.pickupSLAStartDate) errors.pickupSLAStartDate = 'Required';
    if (!values.pickupSLAEndDate) errors.pickupSLAEndDate = 'Required';
    if (!values.deliverySLAStartDate) errors.deliverySLAStartDate = 'Required';
    if (!values.deliverySLAEndDate) errors.deliverySLAEndDate = 'Required';
    if (values.cost == null) errors.cost = 'Required';
    if (!values.bookingPlatform) errors.bookingPlatform = 'Required';
    if (!values.bookingNumber) errors.bookingNumber = 'Required';
    if (!values.allItemsAcknowledged) errors.allItemsAcknowledged = 'Required';
    return errors;
  };

  return (
    <Formik<FormValues>
      initialValues={(defaults as FormValues) ?? {}}
      onSubmit={save}
      validate={validate}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {() => (
        <FormikForm>
          <Panel>
            <Panel.Header>
              <Panel.Title>
                {kind === 'create' ? 'Create Third-Party Transfer' : 'Update Third-Party Transfer'}
              </Panel.Title>
            </Panel.Header>
            <Panel.Body>
              {error && <Alert style="danger">{error}</Alert>}
              {!formData ? (
                <Spinner />
              ) : (
                <>
                  <FormikSelectFormGroup label="Origin Warehouse" id="originWarehouseID" name="originWarehouseID">
                    <option value=""> - Select - </option>
                    {formData.warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </FormikSelectFormGroup>

                  <FormikSelectFormGroup
                    label="Destination Warehouse"
                    id="destinationWarehouseID"
                    name="destinationWarehouseID"
                  >
                    <option value=""> - Select - </option>
                    {formData.warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </FormikSelectFormGroup>

                  <FormikDatePickerFormGroup label="Pickup SLA Start Date" name="pickupSLAStartDate" />
                  <FormikDatePickerFormGroup label="Pickup SLA End Date" name="pickupSLAEndDate" />
                  <FormikTimePickerFormGroup label="Pickup SLA Start Time" name="pickupSLAStartTime" />
                  <FormikTimePickerFormGroup label="Pickup SLA End Time" name="pickupSLAEndTime" />

                  <FormikDatePickerFormGroup label="Delivery SLA Start Date" name="deliverySLAStartDate" />
                  <FormikDatePickerFormGroup label="Delivery SLA End Date" name="deliverySLAEndDate" />
                  <FormikTimePickerFormGroup label="Delivery SLA Start Time" name="deliverySLAStartTime" />
                  <FormikTimePickerFormGroup label="Delivery SLA End Time" name="deliverySLAEndTime" />

                  <FormikInputFormGroup label="Carrier" name="carrier" />
                  <FormikNumberFormGroup label="Cost" name="cost" allowDecimal />
                  <FormikInputFormGroup label="Booking Platform" name="bookingPlatform" />
                  <FormikInputFormGroup label="Booking Number" name="bookingNumber" />

                  {kind === 'create' && (
                    <>
                      <Spacer height="2rem" />
                      <FormikCheckboxFormGroup
                        label="I acknowledge that this will be a transfer of all items belonging to this customer that are still in our possession. This transfer will be attached to a pending long-distance move, if it exists on the account."
                        name="allItemsAcknowledged"
                      />
                    </>
                  )}
                </>
              )}
            </Panel.Body>
            <Panel.Footer align="right">
              <Button kind="primary" type="submit" loading={saving}>
                Save
              </Button>
            </Panel.Footer>
          </Panel>
        </FormikForm>
      )}
    </Formik>
  );
};
